import NarrationProblems from "../studio/NarrationProblems";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { bookKey, can, deleteBook, getBook, listChapters } from "../api";
import BackLink from "../components/BackLink";
import ConfirmDelete from "../components/ConfirmDelete";
import { useT } from "../i18n";
import { primaryOrg, useSession } from "../session";
import BookLanguage from "../studio/BookLanguage";
import BookStory from "../studio/BookStory";
import BookTagsEditor from "../studio/BookTagsEditor";
import BookWritersEditor from "../studio/BookWritersEditor";
import PictureMedium from "../studio/PictureMedium";
import LinkedInstruction from "../studio/LinkedInstruction";
import FreeToggle from "../studio/FreeToggle";
import CoverPanel from "../studio/CoverPanel";
import NarrationSettings from "../studio/NarrationSettings";
import VoicesPanel from "../studio/VoicesPanel";
import ConflictInbox from "../studio/ConflictInbox";
import CharactersPanel from "./CharactersPanel";
import EditionPanel from "./EditionPanel";
import SearchPanel from "./SearchPanel";

export type BookTool =
  "publish" | "characters" | "cover" | "settings" | "conflicts" | "narration" | "voices";

/**
 * BookToolPage is one of the studio's tools for a book, on its own screen.
 *
 * They were all panels stacked under the chapter list, so the book page was
 * six unrelated things on one scroll and the one that mattered most on a
 * phone — the chapters — shared it with a form and three switches. Each tool
 * is now a row on the book page that opens here, with the book one tap back.
 */
export default function BookToolPage({ tool }: { tool: BookTool }) {
  const { bookID = "" } = useParams();
  const { me, isPending } = useSession();
  const org = primaryOrg(me);
  const t = useT();
  const client = useQueryClient();
  const navigate = useNavigate();

  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });
  // For the delete question: how many chapters go with the book.
  const chapters = useQuery({
    queryKey: ["chapters", bookID, "draft"],
    queryFn: () => listChapters(bookID, { draft: true }),
    enabled: tool === "settings",
  });
  const destroy = useMutation({
    mutationFn: () => deleteBook(bookID),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["books"] });
      navigate("/studio");
    },
  });

  if (isPending || book.isPending) {
    return <main className="mx-auto max-w-3xl px-4 py-10" aria-busy="true" />;
  }
  const mine = !!book.data && book.data.org_id === org?.id;
  const allowed =
    mine &&
    (tool === "publish"
      ? can(org, "book.publish")
      : tool === "characters"
        ? can(org, "book.augment")
        : tool === "narration" || tool === "voices"
          ? can(org, "narration.render")
          : tool === "conflicts"
            ? // Reviewing is reading until something is pressed; taking an
              // alternative is checked again by the server as chapter.write.
              can(org, "chapter.write")
            : tool === "cover"
              ? // book.write, not book.augment: uploading a cover spends nothing, and
                // the panel offers only uploading to somebody who cannot spend.
                can(org, "book.update") || can(org, "book.augment")
              : can(org, "narration.render") ||
                can(org, "book.index") ||
                can(org, "book.delete") ||
                can(org, "book.update"));
  if (!allowed) {
    return <Navigate to={`/studio/books/${bookID}`} replace />;
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-10" data-testid={`book-tool-${tool}`}>
      <nav className="mb-6 text-sm">
        <BackLink to={`/studio/books/${bookID}`}>
          <span dir="auto">{book.data?.title}</span>
        </BackLink>
      </nav>
      <h1 className="mb-6 text-2xl font-semibold tracking-tight">{t.studio.tools[tool]}</h1>

      {tool === "publish" && <EditionPanel bookID={bookID} bare />}
      {tool === "conflicts" && <ConflictInbox bookID={bookID} />}
      {tool === "narration" && <NarrationProblems bookID={bookID} />}
      {tool === "voices" && <VoicesPanel bookID={bookID} />}
      {tool === "characters" && (
        <CharactersPanel bookID={bookID} bare locked={!!book.data?.linked_source_id} />
      )}
      {tool === "cover" && (
        <CoverPanel
          bookID={bookID}
          canDescribe={can(org, "book.augment")}
          locked={!!book.data?.linked_source_id}
        />
      )}
      {tool === "settings" && (
        <div className="space-y-10">
          {/* First, and above narration: it is the setting the others read.
              A book whose language is wrong narrates in the right voice and
              has its characters described in the wrong one. */}
          <section>
            <BookLanguage bookID={bookID} />
          </section>
          {/* Who wrote it and who translated it (0118 W2). On a linked book
              too: its writers are its author's, not its source's (ADR-0185). */}
          {can(org, "book.update") && (
            <section>
              <BookWritersEditor bookID={bookID} />
            </section>
          )}
          {/* The words readers filter Browse by (0119 T3), book.update as the
              writers are; a linked book's are its author's (ADR-0185 §4). */}
          {can(org, "book.update") && (
            <section>
              <BookTagsEditor bookID={bookID} />
            </section>
          )}
          <section>
            <BookStory bookID={bookID} />
          </section>
          {/* The one medium the book's pictures are drawn in (ADR-0168). Not
              on a linked book: its pictures are its source's, and nothing is
              described or drawn there. */}
          {can(org, "book.update") && !book.data?.linked_source_id && (
            <section>
              <PictureMedium bookID={bookID} />
            </section>
          )}
          {/* Only a linked book has one, and it takes book.augment because it
              decides what the next refresh pays for — the same permission
              Refresh takes (ADR-0096 §7). */}
          {book.data && can(org, "book.augment") && <LinkedInstruction book={book.data} />}
          {can(org, "narration.render") && (
            <section data-testid="settings-narration">
              <h2 className="text-sm font-medium">{t.narration.settings}</h2>
              <NarrationSettings bookID={bookID} />
            </section>
          )}
          {can(org, "book.index") && (
            <section>
              <SearchPanel bookID={bookID} bare />
            </section>
          )}
          {/* Who may read it without an account (ADR-0070). In settings rather
              than on the publish screen: publishing decides what readers are
              served, this decides who counts as a reader, and the two are
              separate decisions taken at different times. */}
          {can(org, "book.update") && (
            <section>
              <FreeToggle bookID={bookID} />
            </section>
          )}
          {can(org, "book.delete") && (
            <section className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-5">
              <p className="text-xs text-muted">{t.book.deleteNote}</p>
              <ConfirmDelete
                testId="delete-book"
                label={t.book.deleteBook}
                disabled={destroy.isPending}
                question={t.book.deleteBookQuestion(chapters.data?.items.length ?? 0)}
                onConfirm={() => destroy.mutate()}
              />
              {destroy.isError && (
                <p
                  role="alert"
                  data-testid="delete-book-error"
                  className="w-full text-sm text-red-600 dark:text-red-400"
                >
                  {destroy.error instanceof Error ? destroy.error.message : t.book.deleteFailed}
                </p>
              )}
            </section>
          )}
        </div>
      )}
    </main>
  );
}
